// PATH: src/components/questReview/QualityByDocument.tsx
import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Link,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useDocumentsApi, useKnowledgeApi, useQuestReviewApi } from "../../api";
import { learnErrorMessage, type DocumentQuality } from "../../api/questReview/questReview";
import { EmptyState } from "../ui";
import { resolveFacts } from "./factResolution";

/**
 * Par document, sur 30 jours : quêtes signalées, corps rejetés, et les faits
 * les plus cités par ces quêtes. Un fait cité par plusieurs quêtes fausses se
 * corrige une fois, dans l'écran des chapitres, pour tous les élèves — c'est
 * là qu'il faut regarder en premier.
 */
export const QualityByDocument: React.FC = () => {
  const { getQualityByDocument } = useQuestReviewApi();
  const { getDocument } = useDocumentsApi();
  const { getKnowledge } = useKnowledgeApi();
  const [rows, setRows] = React.useState<DocumentQuality[] | null>(null);
  const [titles, setTitles] = React.useState<Record<string, string>>({});
  // `objectif:fait` → texte du fait, lu dans la base de faits actuelle.
  const [factTexts, setFactTexts] = React.useState<Record<string, string>>({});
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setError(null);
    setRows(null);
    try {
      const { data } = await getQualityByDocument(30);
      setRows(data.documents);
      const ids = data.documents.map((d) => d.document_id).filter((id): id is string => Boolean(id));
      const found = await Promise.all(ids.map((id) => getDocument(id).then((r) => [id, r.data.title] as const).catch(() => null)));
      setTitles(Object.fromEntries(found.filter((x): x is readonly [string, string] => Boolean(x))));
      const objectives = Array.from(
        new Set(data.documents.flatMap((d) => d.top_facts.map((f) => f.objective_id)).filter((id): id is string => Boolean(id))),
      );
      const texts: Record<string, string> = {};
      await Promise.all(
        objectives.map(async (objectiveId) => {
          const unit = await getKnowledge(objectiveId, "neighborhood").catch(() => null);
          const ids = data.documents.flatMap((d) => d.top_facts.filter((f) => f.objective_id === objectiveId).map((f) => f.fact_id));
          resolveFacts(unit?.unit ?? null, ids).facts.forEach((f) => {
            texts[`${objectiveId}:${f.id}`] = f.text;
          });
        }),
      );
      setFactTexts(texts);
    } catch (e) {
      setError(learnErrorMessage(e, "Impossible de charger la qualité par document."));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  if (error) {
    return (
      <Alert severity="error" action={<Button onClick={() => void load()}>Réessayer</Button>}>
        {error}
      </Alert>
    );
  }
  if (!rows) return <Skeleton variant="rounded" height={200} />;
  if (rows.length === 0) {
    return <EmptyState title="Aucune quête signalée ni rejetée sur 30 jours" />;
  }
  return (
    <Card variant="outlined">
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Chapitre</TableCell>
              <TableCell align="right">Quêtes signalées</TableCell>
              <TableCell align="right">Signalements en attente</TableCell>
              <TableCell align="right">Quêtes rejetées</TableCell>
              <TableCell>Faits les plus cités</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((d) => (
              <TableRow key={d.document_id ?? "aucun"}>
                <TableCell>
                  {d.document_id ? (
                    <Link component={RouterLink} to={`/chapters?doc=${d.document_id}`} dir="auto">
                      {titles[d.document_id] ?? d.document_id}
                    </Link>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Document inconnu
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">{d.reported_quests}</TableCell>
                <TableCell align="right">{d.pending_reports}</TableCell>
                <TableCell align="right">{d.rejected_bodies}</TableCell>
                <TableCell sx={{ minWidth: 280 }}>
                  {d.top_facts.length === 0 && "—"}
                  {d.top_facts.map((f) => (
                    <Typography key={`${f.objective_id}:${f.fact_id}`} variant="body2" dir="auto">
                      <b>×{f.count}</b> {factTexts[`${f.objective_id}:${f.fact_id}`] ?? <span className="mono">{f.fact_id}</span>}
                    </Typography>
                  ))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Card>
  );
};

export default QualityByDocument;
